import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Badge } from "@/components/common";
import { toast } from "sonner";
import { UserPlus, Trash2, X, Pencil } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 transition";

const emptyAdd = { name: "", email: "", password: "", role: "staff", nomor_rekening: "", bank: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyAdd);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveAdd = async () => {
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Lengkapi data (password min. 6 karakter)");
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Pengguna ditambahkan");
      setAddOpen(false);
      setForm(emptyAdd);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah pengguna");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
        nomor_rekening: editUser.nomor_rekening || "",
        bank: editUser.bank || "",
      });
      toast.success("Data pengguna diperbarui");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus pengguna ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div>
      <PageHeader
        title="Pengguna"
        subtitle="Manajemen Akun & Rekening"
        action={
          <button data-testid="add-user-button" onClick={() => { setForm(emptyAdd); setAddOpen(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition shadow-lg shadow-indigo-600/30">
            <UserPlus className="w-4 h-4" /> Tambah Pengguna
          </button>
        }
      />

      <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden" data-testid="users-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="text-left px-5 py-3">Nama</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-center px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Bank</th>
                <th className="text-left px-5 py-3">No. Rekening</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                  <td className="px-5 py-3 text-white font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-slate-400">{u.email}</td>
                  <td className="px-5 py-3 text-center">
                    <Badge className={u.role === "admin" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}>{u.role}</Badge>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{u.bank || "-"}</td>
                  <td className="px-5 py-3 font-mono text-slate-300">{u.nomor_rekening || "-"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditUser({ ...u })} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded text-slate-400 hover:text-indigo-300 hover:bg-slate-800"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="p-1.5 rounded text-slate-400 hover:text-rose-300 hover:bg-slate-800"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAddOpen(false)} />
          <div className="relative z-10 w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-white">Tambah Pengguna</h2>
              <button onClick={() => setAddOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-xs text-slate-400 mb-1.5">Nama</label><input data-testid="user-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-400 mb-1.5">Email</label><input data-testid="user-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-400 mb-1.5">Kata Sandi (min. 6)</label><input data-testid="user-password-input" type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-slate-400 mb-1.5">Bank</label><input className={inputCls} placeholder="BCA / Mandiri" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
                <div><label className="block text-xs text-slate-400 mb-1.5">No. Rekening</label><input className={inputCls} value={form.nomor_rekening} onChange={(e) => setForm({ ...form, nomor_rekening: e.target.value })} /></div>
              </div>
              <div><label className="block text-xs text-slate-400 mb-1.5">Role</label>
                <select data-testid="user-role-select" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setAddOpen(false)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
              <button data-testid="save-user-button" onClick={saveAdd} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60">{saving ? "Menyimpan..." : "Simpan"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditUser(null)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-white">Edit Pengguna</h2>
              <button onClick={() => setEditUser(null)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-xs text-slate-400 mb-1.5">Nama</label><input data-testid="edit-user-name" className={inputCls} value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} /></div>
              <div><label className="block text-xs text-slate-400 mb-1.5">Email (tidak dapat diubah)</label><input className={inputCls + " opacity-60"} value={editUser.email} disabled /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-slate-400 mb-1.5">Bank</label><input data-testid="edit-user-bank" className={inputCls} placeholder="BCA / Mandiri" value={editUser.bank || ""} onChange={(e) => setEditUser({ ...editUser, bank: e.target.value })} /></div>
                <div><label className="block text-xs text-slate-400 mb-1.5">No. Rekening</label><input data-testid="edit-user-rekening" className={inputCls} value={editUser.nomor_rekening || ""} onChange={(e) => setEditUser({ ...editUser, nomor_rekening: e.target.value })} /></div>
              </div>
              <div><label className="block text-xs text-slate-400 mb-1.5">Role</label>
                <select data-testid="edit-user-role" className={inputCls} value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditUser(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
              <button data-testid="save-edit-user-button" onClick={saveEdit} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60">{saving ? "Menyimpan..." : "Simpan"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
